import React, { Component } from 'react'
import { Form } from 'react-bootstrap'
import '../App.css'
import { embedComponent } from '../embed'
import Field from '../components/Field'
import CurrencyField from '../components/CurrencyField'
import formatCurrency from '../utils/formatters/formatCurrency'
import numberWithLabelChoices from '../utils/forms/numberWithLabelChoices'
import CalculatorResults from '../components/CalculatorResults/index'
import CalculatorResult from '../components/CalculatorResult/index'
import Calculator from '../components/Calculator/index'
import PercentageField from '../components/PercentageField/index'
import CalculatorButtons from '../components/CalculatorButtons/index'
import { minMaxValidator } from '../utils/validators/index'
import formatPercentage from '../utils/formatters/formatPercentage'
import { DEFAULT_MAX_DOLLAR_VALUE } from '../utils/validators'

const popoverMessages = {
  currentLoanAmount: {
    title: 'Current Loan Amount',
    body: [
      'Enter the remaining balance of your current loan.',
    ],
  },
  currentTerm: {
    title: 'Current Term',
    body: [
      'This is the length your current the loan.',
      'Typically, it is 30 years, although it can also be 10 or 15. Loans for periods less than 10 years are more complicated and often involve variable interest rates. <a href="/#/articles/163" target="_blank">Click here to learn about different loan types</a>.',
    ],
  },
  newLoanAmount: {
    title: 'New Loan Amount',
    body: [
      'The new loan balance can be lower or higher if you\'d like to pay down the principal or take cash out.',
    ],
  },
  newTerm: {
    title: 'New Term',
    body: [
      'Your new term is the number of months set for you to pay off your new loan, which replaces your existing mortgage during refinancing. The new term may or may not be the same as your current term.',
    ],
  },
  refinanceFees: {
    title: 'Refinance Fees',
    body: [
      'Like all home loans, a refinance loan requires closing costs to pay for things like the loan origination fee, title and appraisal.',
    ],
  },
}

// Create select input options for dropdown.
const mortgageTermYearValues = [10, 15, 20, 25, 30]
const mortgageTermOptions = numberWithLabelChoices(mortgageTermYearValues, ' years')

class HomeRefinanceCalculator extends Component {
  constructor (props) {
    super(props);

    this.state = HomeRefinanceCalculator.getInitialState();

    this.clearState = this.clearState.bind(this);
    this.onValidation = this.onValidation.bind(this);
    this.getCalculatorValues = this.getCalculatorValues.bind(this);
    this.setCalculatorValues = this.setCalculatorValues.bind(this);
    this.removeEventListeners = this.removeEventListeners.bind(this);
  }

  static getInitialState() {
    return {
      currentLoanAmount: 0,
      currentInterestRate: 0,
      currentTerm: 0,
      newLoanAmount: 0,
      newInterestRate: 0,
      newTerm: 0,
      refinanceFees: 0,
      fieldErrors: {},
    }
  }

  componentDidMount() {
    document.addEventListener('setCalculatorValues', this.setCalculatorValues, true);
    document.addEventListener('getCalculatorValuesHomeRefinanceCalculator', this.getCalculatorValues, true);
    document.addEventListener('removeCalculatorEvents', this.removeEventListeners, true);
  }

  getCalculatorValues (event) {
    this.removeEventListeners();

    // Build newObj off state and exclude fieldErrors
    let newObj = {};
    Object.keys(HomeRefinanceCalculator.getInitialState()).forEach(key => {
      if (key !== 'fieldErrors') {
        newObj[key] = this.state[key];
      }
    });

    newObj.calculatedCurrentMonthlyPayment = this.calculateMonthlyPayment(newObj.currentLoanAmount, newObj.currentTerm, newObj.currentInterestRate);
    newObj.calculatedNewMonthlyPayment = this.calculateMonthlyPayment((parseInt(newObj.newLoanAmount, 10) + parseInt(newObj.refinanceFees, 10)), newObj.newTerm, newObj.newInterestRate);
    newObj.calculatedMonthlySavings = newObj.calculatedCurrentMonthlyPayment - newObj.calculatedNewMonthlyPayment;

    document.dispatchEvent(new CustomEvent('receiveCalculatorStateHomeRefinanceCalculator', { detail: newObj}));
  }

  setCalculatorValues (event) {
    this.handleChange(event.detail.value, event.detail.name);
  }

  removeEventListeners () {
    document.removeEventListener('getCalculatorValuesHomeRefinanceCalculator', this.getCalculatorValues, true);
    document.removeEventListener('setCalculatorValues', this.setCalculatorValues, true);
    document.removeEventListener('removeCalculatorEvents', this.removeEventListeners, true);
  }

  handleChange (value, fieldName) {
    this.setState({ [fieldName]: value });
  }

  clearState () {
    this.setState(HomeRefinanceCalculator.getInitialState());
  }

  onValidation(name, error) {
    if (typeof error !== 'undefined') {
      // Has errors
      if (!this.state.fieldErrors[name]) {
        this.setState({ fieldErrors: Object.assign({}, this.state.fieldErrors, { [name]: error }) });
      }
    } else {
      // No errors
      if (this.state.fieldErrors[name]) {
        const newErrors = Object.assign({}, this.state.fieldErrors);
        delete newErrors[name];
        this.setState({ fieldErrors: newErrors });
      }
    }
  }

  calculateMonthlyPayment (mortgageAmount, mortgageTerm, mortgageInterestRate) {

    const numPayments = mortgageTerm * 12
    const interestRate = mortgageInterestRate / 100 / 12
    const exponentQuantity = Math.pow(1 + interestRate, numPayments)
    const numerator = interestRate * exponentQuantity
    const denominator = exponentQuantity - 1

    return denominator > 0 ? mortgageAmount * (numerator / denominator) : 0
  }

  render () {
    const {
      currentLoanAmount,
      currentInterestRate,
      currentTerm,
      newLoanAmount,
      newInterestRate,
      newTerm,
      refinanceFees,
    } = this.state

    const calculatedCurrentMonthlyPayment = this.calculateMonthlyPayment(currentLoanAmount, currentTerm, currentInterestRate);
    const calculatedNewMonthlyPayment = this.calculateMonthlyPayment((parseInt(newLoanAmount, 10) + parseInt(refinanceFees, 10)), newTerm, newInterestRate);
    const calculatedMonthlySavings = calculatedCurrentMonthlyPayment - calculatedNewMonthlyPayment;
    const hasErrors = Object.keys(this.state.fieldErrors).length > 0;

    return (
      <Calculator title="Refinance Calculator">
        <div className="calculator-body">
          <Form>
            <CurrencyField
              name="currentLoanAmount"
              label="Current Loan Amount"
              value={currentLoanAmount}
              onChange={(e, value) => this.handleChange(value, 'currentLoanAmount') }
              popover={popoverMessages.currentLoanAmount}
              validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
              onValidation={this.onValidation}
            />

            <Field
              type="select"
              name="currentTerm"
              label="Current Term (years)"
              value={currentTerm}
              choices={mortgageTermOptions}
              onChange={(e, value) => this.handleChange(value, 'currentTerm') }
              popover={popoverMessages.currentTerm}
            />

            <PercentageField
              name="currentInterestRate"
              label="Current Interest Rate (%)"
              value={currentInterestRate}
              onChange={(e, value) => this.handleChange(value, 'currentInterestRate') }
              validator={minMaxValidator(0.001, 30, formatPercentage)}
              onValidation={this.onValidation}
            />

            <CurrencyField
              name="newLoanAmount"
              label="New Loan Amount"
              value={newLoanAmount}
              onChange={(e, value) => this.handleChange(value, 'newLoanAmount') }
              popover={popoverMessages.newLoanAmount}
              validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
              onValidation={this.onValidation}
            />

            <Field
              type="select"
              name="newTerm"
              label="New Term (years)"
              value={newTerm}
              choices={mortgageTermOptions}
              onChange={(e, value) => this.handleChange(value, 'newTerm') }
              popover={popoverMessages.newTerm}
            />

            <PercentageField
              name="newInterestRate"
              label="New Interest Rate (%)"
              value={newInterestRate}
              onChange={(e, value) => this.handleChange(value, 'newInterestRate') }
              validator={minMaxValidator(0.001, 30, formatPercentage)}
              onValidation={this.onValidation}
            />

            <CurrencyField
              name="refinanceFees"
              label="Refinance Fees"
              value={refinanceFees}
              onChange={(e, value) => this.handleChange(value, 'refinanceFees') }
              popover={popoverMessages.refinanceFees}
              validator={minMaxValidator(0, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
              onValidation={this.onValidation}
            />
          </Form>
          <CalculatorResults>
            <CalculatorResult
                label={'Refinancing could save this much a month'}
                value={formatCurrency(Math.ceil((!hasErrors && calculatedMonthlySavings) || 0))}
            />
          </CalculatorResults>
          <CalculatorButtons onClear={this.clearState} />
        </div>
      </Calculator>
    )
  }
}

embedComponent(HomeRefinanceCalculator, 'bmm-home-refinance-calc', 'bmm-home-refinance-calc-embed')

export default HomeRefinanceCalculator



// WEBPACK FOOTER //
// ./src/calculators/HomeRefinanceCalculator.js